import {inject, Injectable} from '@angular/core';
import {CreateFormDataParams, FileDownloadOptions, FileTypeName} from "../types/file.types";
import {FilesApiService} from "../services-api/files-api.service";
import {mimeTypeMap} from "../components/modals/files-modal/configs/files-modal-config";
import {ErrorService} from "./error.service";
import {ModalsService} from "./modals.service";
import {FilesStatus, GetFilesStatusParams} from "../types/files-status.types";
import {forbiddenExtensions} from "../models/forbidden-extensions-data";
import {imageExtensions} from "../models/image-extensions";

@Injectable({
  providedIn: 'root'
})
export class FilesService {
  private filesApiService: FilesApiService = inject(FilesApiService);
  private errorService: ErrorService = inject(ErrorService);
  private modalsService: ModalsService = inject(ModalsService);

  public initiateFileDownload({ file, fileName }: FileDownloadOptions): void {
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  public createFormData({ file, fileType }: CreateFormDataParams): FormData {
    const formData = new FormData();
    formData.append(fileType, file, file.name);
    formData.append('data', JSON.stringify({ file: file.name }));
    return formData;
  }

  public filterAllowedFiles(files: Array<File>, type: FileTypeName): Array<File> {
    const allowedFiles = files.filter(file => {
      const fileName = file.name.toLowerCase();
      return type === 'PRODUCT_CARD' ?
        imageExtensions.some(ext => fileName.endsWith(ext)) :
        !forbiddenExtensions.some(ext => fileName.endsWith(ext));
    });

    if (!allowedFiles.length) {
      this.errorService.showErrorMessage('Все файлы содержат запрещённое расширение!');
      return [];
    }

    if (allowedFiles.length < files.length) {
      this.errorService.showErrorMessage('Некоторые файлы не были загружены (запрещённые расширения)!');
    }

    return allowedFiles;
  }

  public downloadFileNoEvents(id: number): Promise<string> {
    return new Promise((resolve, reject) => {
      this.filesApiService.downloadFileNoEvents(id).subscribe({
        next: (blob) => resolve(URL.createObjectURL(blob)),
        error: (error) => reject(error),
      });
    });
  }

  public fetchFilesStatus(getFilesStatusParams: GetFilesStatusParams): Promise<FilesStatus> {
    return new Promise((resolve, reject) => {
      this.filesApiService.fetchFilesStatus(getFilesStatusParams).subscribe({
        next: (filesStatus) => resolve(filesStatus),
        error: (error) => reject(error)
      });
    });
  }

  public async handleFileBlob(fileDownloadOptions: FileDownloadOptions): Promise<void> {
    const { file, fileName, fileId } = fileDownloadOptions;
    let mimeType = file.type;
    if (!mimeType || mimeType === 'application/octet-stream') {
      const extension = fileName?.split('.').pop()?.toLowerCase() || '';
      mimeType = mimeTypeMap[extension] || this.guessMimeType(extension, fileName);
    }

    const correctedBlob = new Blob([file], { type: mimeType });
    const type = mimeType.split('/');
    if (['video', 'image'].includes(type[0]) || mimeType === 'application/pdf') {
      const fileUrl = window.URL.createObjectURL(correctedBlob);
      try {
        await this.modalsService.openPreviewFileModal({
          type: mimeType.split('/'),
          fileId: fileId,
          url: fileUrl
        });
      } finally {
        window.URL.revokeObjectURL(fileUrl);
      }
    } else {
      this.errorService.showErrorMessage('Открытие файла заблокировано. Файл будет скачан.');
      this.initiateFileDownload({ file: correctedBlob, fileName });
    }
  }

  public downloadArchiveFiles(fileIds: Array<number>): Promise<void> {
    return new Promise((resolve, reject) => {
      this.filesApiService.downloadArchiveFiles(fileIds).subscribe({
        next: (blob) => {
          this.downloadArchive(blob);
          resolve();
        },
        error: (error) => reject(error)
      });
    });
  }

  private guessMimeType(extension: string, fileName: string): string {
    const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'flv', 'wmv'];
    if (videoExtensions.includes(extension) || /video/i.test(fileName)) {
      return mimeTypeMap[extension] || 'video/mp4';
    }

    return 'application/octet-stream';
  }

  private downloadArchive(blob: Blob, prefix: string = 'archive') {
    const formattedDate = new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(new Date()).replace(/\./g, '-');
    const fileName = `${prefix}_${formattedDate}.zip`;
    this.initiateFileDownload({
      file: blob,
      fileName: fileName
    });
  }
}
